/** E2E-FIX1 (GAP-10) : « Charger la simulation » sur des tables déjà remplies.
 * - La simulation déclarée est déjà celle des tables (mêmes lignes, même révision) : le serveur répond « déjà chargée »
 *   (`alreadyLoaded`) sans rien écrire, l'étape du plan est terminée.
 * - Sinon le refus `module.data.confirm_replace` nomme ce qui occupe les tables (lignes par autre simulation, lignes sans
 *   simulation) et le Maker choisit l'un des deux gestes, avec les commandes existantes (confirmées et journalisées par le
 *   serveur) : décharger les autres simulations puis charger celle-ci, ou ajouter aux données existantes (case « Autoriser »).
 * Générique : aucun module, aucune table ni aucune simulation nommés ici. */
export type LoadOccupants = { simulations: { id: string; rows: number }[]; otherRows: number };
export type LoadChoice = 'unload-others' | 'add';
export const LOAD_CHOICES: LoadChoice[] = ['unload-others', 'add'];
export const LOAD_REFUSAL = 'module.data.confirm_replace';
/** E2E-QUIZ-CMDB (GAP-QE2E-2) : refus de ModuleDataWriter quand le module n'a pas de tables dans cet environnement (non installé). */
export const LOAD_NOT_INSTALLED = 'module.data.not_installed';
export function simulationLoadNotInstalled(cause: unknown): boolean {
  return ((cause as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code) === LOAD_NOT_INSTALLED;
}

/** Les occupants d'un refus de chargement, ou null pour tout autre échec (qui reste une erreur ordinaire). */
export function readLoadRefusal(cause: unknown): LoadOccupants | null {
  const data = (cause as { response?: { data?: unknown } } | null)?.response?.data as { code?: unknown; details?: unknown } | undefined;
  if (!data || data.code !== LOAD_REFUSAL) return null;
  const details = (data.details ?? {}) as { simulations?: unknown; otherRows?: unknown };
  const simulations = Array.isArray(details.simulations) ? details.simulations.flatMap(item => {
    const { id, rows } = (item ?? {}) as { id?: unknown; rows?: unknown };
    return typeof id === 'string' && /^[0-9a-f-]{36}$/i.test(id) && typeof rows === 'number' && Number.isSafeInteger(rows) && rows >= 0 ? [{ id, rows }] : [];
  }) : [];
  const otherRows = typeof details.otherRows === 'number' && Number.isSafeInteger(details.otherRows) && details.otherRows >= 0 ? details.otherRows : 0;
  return { simulations, otherRows };
}

/** « Décharger les autres » n'aboutit que si seules des simulations occupent les tables (une ligne sans simulation resterait). */
export function choiceAvailable(choice: LoadChoice, occupants: LoadOccupants): boolean {
  return choice === 'add' || occupants.simulations.length > 0 && occupants.otherRows === 0;
}

export function alreadyLoaded(result: unknown): boolean {
  return !!result && typeof result === 'object' && (result as { alreadyLoaded?: unknown }).alreadyLoaded === true;
}

type Run = (key: 'unload-simulation' | 'load-simulation', parameters: Record<string, unknown>) => Promise<unknown>;
/** Le geste choisi, avec les commandes existantes et dans l'ordre : chaque déchargement puis le chargement. */
export async function resolveLoadChoice(run: Run, simulationId: string, occupants: LoadOccupants, choice: LoadChoice): Promise<{ unloaded: string[]; result: unknown }> {
  if (!choiceAvailable(choice, occupants)) throw new Error('studio.command.load.choice_unavailable');
  const unloaded: string[] = [];
  if (choice === 'unload-others') for (const simulation of occupants.simulations) {
    if (simulation.id === simulationId) continue;
    await run('unload-simulation', { simulationId: simulation.id });
    unloaded.push(simulation.id);
  }
  const result = await run('load-simulation', { simulationId, confirmReplace: choice === 'add' });
  return { unloaded, result };
}
